import {Icon, Popover} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {useMemo, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {MAP_LAYERS} from "../map/MapAnchor";
import ActionButton, {ContainerPosition} from "../map/action/ActionButton";
import {RoadIcon, SatelliteIcon, TrafficLightIcon} from "../map/action/MapIcons";
import {MapActionBreakPoint} from "../map/action/MapActionBar";

type MapTypeIcon = {
  title: string;
  mapType: MAP_LAYERS;
  icon: JSX.Element;
};

const MapTypeIcons: MapTypeIcon[] = [
  {title: "Roadmap", mapType: MAP_LAYERS.ROADMAP, icon: <RoadIcon />},
  {title: "Satellite", mapType: MAP_LAYERS.HYBRID, icon: <SatelliteIcon />}
];

const tooltipContent = {
  toggleAll: {
    active: "Click to hide all routes",
    inactive: "Click to show all routes"
  },
  traffic: {
    active: "Click to hide traffic",
    inactive: "Click to show traffic"
  },
  mapType: {
    active: "Click to select a different map type"
  }
};

type RoutePlanningActionBarProps = {
  mapResponsiveWidth: number;
  mapType: string;
  isTrafficActive: boolean;
  allHidden: boolean;
  noRoutes?: boolean;
  onTrafficClicked: () => void;
  onChangeMapType: (type: string) => void;
  onToggleAll: () => void;
  newWindow?: Window;
  usePortal?: boolean;
};

const RoutePlanningActionBar = ({
  mapResponsiveWidth,
  mapType,
  isTrafficActive,
  allHidden,
  noRoutes,
  onTrafficClicked,
  onChangeMapType,
  onToggleAll,
  newWindow,
  usePortal
}: RoutePlanningActionBarProps) => {
  const [isOpenMapTypeMenu, setIsOpenMapTypeMenu] = useState(false);

  const isHideMenuText = useMemo(() => mapResponsiveWidth < MapActionBreakPoint.HIDETEXT, [mapResponsiveWidth]);
  const switchToVerticalMenu = useMemo(
    () => mapResponsiveWidth < MapActionBreakPoint.VERTICAL_MENU,
    [mapResponsiveWidth]
  );
  const isHideMenu = useMemo(() => mapResponsiveWidth < MapActionBreakPoint.HIDE_MENU, [mapResponsiveWidth]);

  const currentSelectedMapType = useMemo(
    () => MapTypeIcons.find((v) => v.mapType === mapType) as MapTypeIcon,
    [mapType]
  );

  const onMapTypeClick = (type: string) => {
    onChangeMapType(type);
    setIsOpenMapTypeMenu(false);
  };

  const renderMapTypeMenuItems = () =>
    MapTypeIcons.filter((v) => v.mapType !== mapType).map((value) => (
      <ActionButton
        dataTestId={`map-type-item-${value.mapType}`}
        key={value.title}
        active={false}
        onClick={() => onMapTypeClick(value.mapType)}
        icon={value.icon}
        isHideText={isHideMenuText}
        text={value.title}
        newWindow={newWindow}
        usePortal={usePortal}
      />
    ));

  if (isHideMenu) return null;

  return (
    <Container data-testid="route-planning-action-bar">
      <ActionsContainer switchToVertical={switchToVerticalMenu}>
        <ActionButton
          dataTestId="map-action-toggle-all"
          active={!allHidden && !noRoutes}
          onClick={noRoutes ? () => {} : onToggleAll}
          icon={
            <FontAwesomeIcon icon={allHidden ? solid("eye") : solid("eye-slash")} style={{width: 14, height: 14}} />
          }
          tooltipContent={tooltipContent.toggleAll}
          isHideText={isHideMenuText}
          text={allHidden ? "Show All" : "Hide All"}
          containerPosition={ContainerPosition.LEFT}
          switchToVerticalMenu={switchToVerticalMenu}
          newWindow={newWindow}
          usePortal={usePortal}
        />
      </ActionsContainer>
      <ActionsContainer isOpenMapTypeMenu={isOpenMapTypeMenu} switchToVertical={switchToVerticalMenu}>
        <ActionButton
          dataTestId="map-action-traffic"
          active={isTrafficActive}
          onClick={onTrafficClicked}
          icon={<TrafficLightIcon />}
          tooltipContent={tooltipContent.traffic}
          isHideText={isHideMenuText}
          text="Traffic"
          containerPosition={ContainerPosition.RIGHT}
          switchToVerticalMenu={switchToVerticalMenu}
          newWindow={newWindow}
          usePortal={usePortal}
        />
        <Popover
          portalContainer={newWindow?.document.body}
          usePortal={usePortal}
          content={<>{renderMapTypeMenuItems()}</>}
          position="bottom"
          minimal={true}
          matchTargetWidth={true}
          popoverClassName="map-action-bar-popover"
          isOpen={isOpenMapTypeMenu}
          disabled={isHideMenu}
        >
          <ActionButton
            dataTestId="map-action-maptype"
            onClick={() => setIsOpenMapTypeMenu(!isOpenMapTypeMenu)}
            active={true}
            icon={currentSelectedMapType?.icon}
            tooltipContent={tooltipContent.mapType}
            isHideText={isHideMenuText}
            rigthIcon={<Icon data-testid="maptype-chevron" icon={isOpenMapTypeMenu ? "chevron-up" : "chevron-down"} />}
            text={
              <>
                {currentSelectedMapType?.title} &nbsp;&nbsp;
                <Icon data-testid="maptype-chevron-text" icon={isOpenMapTypeMenu ? "chevron-up" : "chevron-down"} />
              </>
            }
            containerPosition={ContainerPosition.RIGHT}
            switchToVerticalMenu={switchToVerticalMenu}
            newWindow={newWindow}
            usePortal={usePortal}
          />
        </Popover>
      </ActionsContainer>
    </Container>
  );
};

export default RoutePlanningActionBar;

type ActionsContainerProps = {isOpenMapTypeMenu?: boolean; switchToVertical?: boolean};

const Container = styled.div`
  font-family: "Roboto", sans-serif !important;
  z-index: 1000;
  inset: 12px 12px auto 12px;
  position: absolute;
  display: flex;
  justify-content: space-between;
  gap: 5px;
`;

const ActionsContainer = styled.div<ActionsContainerProps>`
  box-shadow: 0px 2px 2px 0px #00000040;
  border-radius: ${(props) => (props.isOpenMapTypeMenu ? "10px 10px 0 10px" : "10px")};
  overflow: hidden;
  display: grid;
  grid-auto-flow: ${(props) => (props.switchToVertical ? "row" : "column")};
  grid-auto-columns: minmax(0, 1fr);
  background-color: transparent;
  height: ${(props) => (props.switchToVertical ? "fit-content" : "100%")};
  & .bp5-popover-target {
    height: ${(props) => (props.switchToVertical ? "fit-content" : "100%")};
    width: 100%;
  }

  & > .bp5-popover-target:not(:last-child) {
    & > .bp5-button {
      border-right: 2px solid #0e0e0e14 !important;
    }

    & > .bp5-button.bp5-active {
      border-right: 2px solid #35578a !important;
    }
  }
`;
